import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Bot,
  UserPlus,
  Calendar,
  ClipboardList,
  CreditCard,
  MapPin,
  Send,
  Home,
  Circle,
  Plus,
  MessageSquare,
  LogOut,
  User,
  Settings,
  ChevronDown,
  Mail,
  Phone,
  Shield
} from 'lucide-react';
// User side imports
import {
  Login,
  PatientRegistrationModal,
  AppointmentModal,
  MyAppointmentsModal,
  ServicesPricingModal,
  NearbyHospitalsModal,
  UHIDSelector
} from './user';

// Admin side imports
import { AdminPanel } from './admin';
import sessionManager from './services/SessionManager';

const ArogyaUI = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [message, setMessage] = useState('');
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [isMyAppointmentsModalOpen, setIsMyAppointmentsModalOpen] = useState(false);
  const [isServicesPricingModalOpen, setIsServicesPricingModalOpen] = useState(false);
  const [isNearbyHospitalsModalOpen, setIsNearbyHospitalsModalOpen] = useState(false);
  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [themeSettings, setThemeSettings] = useState(null);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState('');
  const [appointmentAction, setAppointmentAction] = useState('');
  const [selectedUHID, setSelectedUHID] = useState('');
  const [selectedPatientDetails, setSelectedPatientDetails] = useState(null);
  const [uhidRefreshTrigger, setUhidRefreshTrigger] = useState(0);
  const [chatHistoryRefreshTrigger, setChatHistoryRefreshTrigger] = useState(0);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const currentUserRef = useRef(null);

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  const refreshCurrentUser = useCallback(async () => {
    try {
      const result = await sessionManager.validateSession();

      if (result.valid && result.user) {
        setIsAuthenticated(true);
        setCurrentUser(prev => {
          if (!prev) {
            return result.user;
          }

          const prevString = JSON.stringify(prev);
          const nextString = JSON.stringify(result.user);
          if (prevString === nextString) {
            return prev;
          }
          return result.user;
        });
        return result.user;
      }

      if (result.reason === 'network_error') {
        console.warn('Network error while validating session; keeping current state.');
        return currentUserRef.current;
      }

      if (result.reason === 'unauthorized') {
        sessionManager.clearSession();
      }

      setIsAuthenticated(false);
      setCurrentUser(null);
      return null;
    } catch (error) {
      console.error('Error refreshing user session:', error);
      return null;
    }
  }, []);

  // Sidebar state with localStorage persistence
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    return saved !== null ? JSON.parse(saved) : true; // Default to open
  });

  const [sidebarPosition, setSidebarPosition] = useState(() => {
    const saved = localStorage.getItem('sidebarPosition');
    return saved || 'left'; // Default to left
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileDropdownOpen && !event.target.closest('.profile-dropdown-container')) {
        setIsProfileDropdownOpen(false);
      }
    };

    if (isProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileDropdownOpen]);

  // Handle session events
  const handleSessionEvent = useCallback((event, data) => {
    switch (event) {
      case 'logout':
      case 'session_expired':
      case 'session_invalid':
        // Clear authentication state
        setIsAuthenticated(false);
        setCurrentUser(null);

        // Clear ALL session data - destroy everything
        setChatMessages([]);
        setMessage('');
        setSelectedAppointmentId('');
        setAppointmentAction('');
        setSelectedUHID('');
        setSelectedPatientDetails(null);

        // Clear selected UHID from localStorage on session end
        localStorage.removeItem('selectedUHID');
        localStorage.removeItem('selectedPatientDetails');

        // Close all modals
        setIsRegistrationModalOpen(false);
        setIsAppointmentModalOpen(false);
        setIsMyAppointmentsModalOpen(false);
        setIsServicesPricingModalOpen(false);
        setIsNearbyHospitalsModalOpen(false);
        break;
      case 'login':
        // Another tab logged in, refresh session from backend
        refreshCurrentUser();
        break;
      default:
        break;
    }
  }, [refreshCurrentUser]);

  useEffect(() => {
  // Maintain one consistent thread ID per user
  let existingThread = localStorage.getItem("threadId");

  if (!existingThread) {
    const newThread = generateChatSessionId();
    localStorage.setItem("threadId", newThread);
    existingThread = newThread;
  }

  setChatSessionId(existingThread);

  // Keep sessionId stable (only set once)
  const sessionID = sessionId || generateChatSessionId();
  setSessionId(sessionID);
}, []);


  useEffect(() => {
    // Register session event listener
    sessionManager.addListener(handleSessionEvent);

    // Hydrate session from backend using cookie
    refreshCurrentUser();

    // Restore selected UHID from localStorage
    const savedUHID = localStorage.getItem('selectedUHID');
    const savedPatientDetails = localStorage.getItem('selectedPatientDetails');

    if (savedUHID && savedPatientDetails) {
      try {
        const patientDetails = JSON.parse(savedPatientDetails);
        setSelectedUHID(savedUHID);
        setSelectedPatientDetails(patientDetails);
        console.log('🔄 Restored selected UHID from localStorage:', savedUHID);
      } catch (e) {
        console.error('Error parsing saved patient details:', e);
        localStorage.removeItem('selectedUHID');
        localStorage.removeItem('selectedPatientDetails');
      }
    }

    // Get or create session ID from SessionManager
    const sessionId = sessionManager.getSessionId();
    setSessionId(sessionId);

    // NOTE: chatSessionId is already initialized in the first useEffect from localStorage
    // DO NOT create a new one here - this was causing multiple Azure threads to be created!

    // Fetch theme settings
    fetchThemeSettings();

    // Add keyboard shortcut for admin panel (Ctrl+Shift+A)
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setIsAdminPanelOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    // Cleanup on unmount
    return () => {
      sessionManager.removeListener(handleSessionEvent);
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleSessionEvent, refreshCurrentUser]);

  const fetchThemeSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/backend/api/admin/settings`, {
        method: 'GET',
        credentials: 'include'
      

      });

      if (!response.ok) {
        console.warn(`Failed to fetch theme settings: ${response.status}`);
        return;
      }

      const data = await response.json();
      if (data.success && data.settings) {
        setThemeSettings(data.settings);
      }
    } catch (error) {
      console.error('Error fetching theme settings:', error);
    }
  };

  const handleSettingsUpdate = (newSettings) => {
    setThemeSettings(newSettings);
  };

  const handleLogin = (user) => {
    if (user) {
      sessionManager.saveAuthData({ user });
      setCurrentUser(user);
    } else {
      refreshCurrentUser();
    }

    setIsAuthenticated(true);

    // Clear ALL previous session data for fresh start (except selected UHID - let it persist)
    setChatMessages([]);
    setMessage('');
    setSelectedAppointmentId('');
    setAppointmentAction('');
    // DON'T clear selectedUHID - let it persist across logins
    // setSelectedUHID('');
    // setSelectedPatientDetails(null);

    // Close all modals
    setIsRegistrationModalOpen(false);
    setIsAppointmentModalOpen(false);
    setIsMyAppointmentsModalOpen(false);
    setIsServicesPricingModalOpen(false);
    setIsNearbyHospitalsModalOpen(false);

    // Generate new chat session ID for new login
    const newChatSessionId = generateChatSessionId();
    setChatSessionId(newChatSessionId);

    // Trigger UHID list refresh after login
    console.log('✅ User logged in - refreshing UHID list');
    refreshUHIDList();
  };

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/backend/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Clear session through session manager
    sessionManager.clearSession();

    // Clear authentication state
    setIsAuthenticated(false);
    setCurrentUser(null);

    // Clear ALL session data - destroy everything for fresh start
    setChatMessages([]);
    setMessage('');
    setSelectedAppointmentId('');
    setAppointmentAction('');
    setSelectedUHID('');
    setSelectedPatientDetails(null);

    // Clear selected UHID from localStorage on logout
    localStorage.removeItem('selectedUHID');
    localStorage.removeItem('selectedPatientDetails');
    console.log('🗑️ Cleared selected UHID on logout');

    // Close all modals
    setIsRegistrationModalOpen(false);
    setIsAppointmentModalOpen(false);
    setIsMyAppointmentsModalOpen(false);
    setIsServicesPricingModalOpen(false);
    setIsNearbyHospitalsModalOpen(false);

    // Generate new session ID for next login
    const newSessionId = sessionManager.getSessionId();
    setSessionId(newSessionId);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'numeric', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const generateChatSessionId = () => {
    // Generate a unique chat session ID
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);
    return `chat_${timestamp}_${random}`;
  };

  const handleNewChat = async () => {
    const oldChatId = chatSessionId;

    // Generate new chat session ID first
    const newChatId = generateChatSessionId();

    // Save current chat session and start new session with clean context
    if (currentUser) {
      try {
        // Call the new-session endpoint to properly clear AI context
        await fetch(`${process.env.REACT_APP_API_URL}/backend/api/chat/new-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            old_chat_id: oldChatId,
            new_chat_id: newChatId,
            user_id: currentUser.user_id || currentUser.id
          })
        });
        console.log('Started new chat session with clean context:', newChatId);
      } catch (error) {
        console.error('Error starting new chat session:', error);
      }
    }

    // Clear UI immediately
    setChatMessages([]);
    setChatSessionId(newChatId);
    setMessage('');

    // Update localStorage with new chat ID for persistence
    localStorage.setItem("threadId", newChatId);
    console.log('💾 New chat ID saved to localStorage:', newChatId);

    // NOTE: Keep sessionId stable - only chatSessionId should change for new chats
    // This prevents creating unnecessary Azure threads
  };

  const handleUHIDSelect = (uhid, patientDetails) => {
    setSelectedUHID(uhid);
    setSelectedPatientDetails(patientDetails);

    // Persist selected UHID to localStorage
    if (uhid && patientDetails) {
      localStorage.setItem('selectedUHID', uhid);
      localStorage.setItem('selectedPatientDetails', JSON.stringify(patientDetails));
      console.log('✅ Selected UHID saved to localStorage:', uhid);
    } else {
      localStorage.removeItem('selectedUHID');
      localStorage.removeItem('selectedPatientDetails');
      console.log('🗑️ Cleared UHID selection from localStorage');
    }

    console.log('Selected UHID:', uhid, 'Details:', patientDetails);
  };

  const handleAppointmentSelect = (appointmentId, action = 'reschedule') => {
    setSelectedAppointmentId(appointmentId);
    setAppointmentAction(action);
    console.log('✅ Selected Appointment ID:', appointmentId, 'Action:', action);
    // Open appointment modal with the selected action
    setIsAppointmentModalOpen(true);
  };

  const handleAppointmentSelectWithoutModal = (appointmentId, action = 'reschedule') => {
    setSelectedAppointmentId(appointmentId);
    setAppointmentAction(action);
    console.log('✅ Selected Appointment ID:', appointmentId, 'Action:', action);
  };

  const refreshUHIDList = () => {
    console.log('🔄 Triggering UHID list refresh...');
    setUhidRefreshTrigger(prev => prev + 1);
  };

  const refreshChatHistory = () => {
    console.log('🔄 Triggering chat history refresh...');
    setChatHistoryRefreshTrigger(prev => prev + 1);
  };

  const handleSelectChat = async (chat) => {
    try {
      console.log('🔄 Loading chat:', chat.chat_id);

      // Fetch full chat session from database
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/backend/api/chat/cosmos/history?chat_id=${chat.chat_id}`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();

        if (data.success && data.chat_sessions && data.chat_sessions.length > 0) {
          const chatSession = data.chat_sessions[0];

          // Load messages into chat UI
          if (chatSession.messages && chatSession.messages.length > 0) {
            // Messages are stored as query/response pairs
            const formattedMessages = chatSession.messages.flatMap(msg => [
              {
                type: 'user',
                content: msg.query,
                timestamp: new Date(msg.timestamp)
              },
              {
                type: 'bot',
                content: msg.response,
                timestamp: new Date(msg.timestamp)
              }
            ]);

            setChatMessages(formattedMessages);
            console.log(`✅ Loaded ${formattedMessages.length} messages from chat history`);
          }

          // Set the chat_id to continue this conversation
          setChatSessionId(chat.chat_id);

          console.log('✅ Chat loaded successfully');
        }
      }
    } catch (error) {
      console.error('Error loading chat:', error);
    }
  };

  const handlePatientRegistrationSuccess = (newUHID) => {
    console.log('✅ Patient registration successful - refreshing UHID list');
    refreshUHIDList();

    // Don't add system messages to chat - keep UI actions separate from chat
    // The UHID list will update automatically, no need to notify chat
  };

  const handleAppointmentSuccess = (appointmentId) => {
    console.log('✅ Appointment booked successfully - refreshing UHID list');
    refreshUHIDList();

    // Don't add system messages to chat - keep UI actions separate from chat
    // Appointments will be visible in "My Appointments" section
  };

  // Sidebar control functions
  const handleSidebarToggle = (isOpen) => {
    setIsSidebarOpen(isOpen);
    localStorage.setItem('sidebarOpen', JSON.stringify(isOpen));
  };

  const handleSidebarPositionChange = (position) => {
    setSidebarPosition(position);
    localStorage.setItem('sidebarPosition', position);
  };

  const serviceButtons = [
    { id: 'registration', icon: UserPlus, text: 'New Patient Registration', color: 'bg-blue-50 text-blue-600 border-blue-200' },
    { id: 'bookAppointment', icon: Calendar, text: 'Book Appointment', color: 'bg-purple-50 text-purple-600 border-purple-200' },
    { id: 'myAppointments', icon: ClipboardList, text: 'My Appointments', color: 'bg-orange-50 text-orange-600 border-orange-200' },
    { id: 'services', icon: CreditCard, text: 'Services & Pricing', color: 'bg-red-50 text-red-600 border-red-200' },
    { id: 'nearbyHospitals', icon: MapPin, text: 'Nearby Hospitals', color: 'bg-green-50 text-green-600 border-green-200' }
  ];

  const handleServiceClick = (serviceId) => {
    switch(serviceId) {
      case 'registration':
        setIsRegistrationModalOpen(true);
        break;
      case 'bookAppointment':
        setIsAppointmentModalOpen(true);
        break;
      case 'myAppointments':
        setIsMyAppointmentsModalOpen(true);
        break;
      case 'services':
        setIsServicesPricingModalOpen(true);
        break;
      case 'nearbyHospitals':
        setIsNearbyHospitalsModalOpen(true);
        break;
      default:
        break;
    }
  };


  const handleSendMessage = async () => {
    if (message.trim() && !isLoading) {
      const userMessage = message.trim();
      setMessage('');
      setIsLoading(true);

      // Add user message to chat
      setChatMessages(prev => [...prev, { type: 'user', content: userMessage, timestamp: new Date() }]);

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/backend/api/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            message: userMessage,
            session_id: sessionId,
            chat_id: chatSessionId,
            user_id: currentUser?.user_id || currentUser?.id,
            user_email: currentUser?.email,
            user_phone: currentUser?.phoneNumber,
            is_admin: currentUser?.is_admin || false,
            uhid: selectedUHID  // Send selected UHID with chat message
          })
        });

        // Check if response is ok (status 200-299)
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
          const errorMessage = errorData.error || errorData.message || `Server error: ${response.status}`;
          console.error('API Error:', errorMessage);

          setChatMessages(prev => [...prev, {
            type: 'bot',
            content: errorMessage,
            timestamp: new Date()
          }]);
          return;
        }

        const data = await response.json();
        console.log('API Response:', data);

        if (data.success) {
          // Add bot response to chat
          const botMessage = data.message || data.reply;
          if (botMessage) {
            setChatMessages(prev => [...prev, {
              type: 'bot',
              content: botMessage,
              timestamp: new Date()
            }]);

            // Extract UHID from bot response (pattern: 2-3 uppercase letters + 9-12 digits)
            const uhidPattern = /\b[A-Z]{2,3}\d{9,12}\b/g;
            const uhidMatches = botMessage.match(uhidPattern);

            // Extract Appointment ID from bot response (pattern: APT + 2 letters + 5 digits)
            const appointmentPattern = /\bAPT[A-Z]{2}\d{5}\b/g;
            const appointmentMatches = botMessage.match(appointmentPattern);

            // Check if bot response indicates successful patient registration or appointment booking
            const isPatientRegistered = botMessage.toLowerCase().includes('patient registered') ||
                                       botMessage.toLowerCase().includes('uhid') ||
                                       botMessage.toLowerCase().includes('registration successful');

            const isAppointmentBooked = botMessage.toLowerCase().includes('appointment') &&
                                       (botMessage.toLowerCase().includes('booked') ||
                                        botMessage.toLowerCase().includes('scheduled') ||
                                        botMessage.toLowerCase().includes('confirmed'));

            // If UHID or Appointment was created through chat, refresh the UHID list
            if ((uhidMatches && isPatientRegistered) || (appointmentMatches && isAppointmentBooked)) {
              console.log('✅ Detected new UHID/Appointment creation via chat - refreshing list');

              // Refresh UHID list to show the new patient/appointment
              refreshUHIDList();

              // Log the extracted IDs
              if (uhidMatches && isPatientRegistered) {
                console.log('📋 Extracted UHIDs from chat:', uhidMatches);
              }
              if (appointmentMatches && isAppointmentBooked) {
                console.log('📅 Extracted Appointment IDs from chat:', appointmentMatches);
              }
            }

            // Refresh chat history in sidebar after successful message
            refreshChatHistory();
          } else {
            console.error('No message in successful response:', data);
            setChatMessages(prev => [...prev, {
              type: 'bot',
              content: 'Received empty response from server.',
              timestamp: new Date()
            }]);
          }
        } else {
          // Add error message from API
          const errorMessage = data.error || data.message || 'An error occurred. Please try again.';
          console.error('API returned error:', errorMessage);
          setChatMessages(prev => [...prev, {
            type: 'bot',
            content: errorMessage,
            timestamp: new Date()
          }]);
        }
      } catch (error) {
        console.error('Chat error:', error);
        setChatMessages(prev => [...prev, {
          type: 'bot',
          content: `Connection error: ${error.message}. Please check your connection and try again.`,
          timestamp: new Date()
        }]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: themeSettings?.mainBackgroundColor || '#f9fafb' }}>
      {/* Header */}
      <div className="text-white px-6 py-4" style={{
        background: themeSettings
          ? `linear-gradient(to right, ${themeSettings.headerBackgroundStart}, ${themeSettings.headerBackgroundEnd})`
          : 'linear-gradient(to right, #2563eb, #1d4ed8)'
      }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center p-1">
              <img src="/apollo-logo.svg" alt="Apollo Hospitals" className="w-10 h-10 object-contain" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Apollo Cancer Care Center</h1>
              <p className="text-blue-100 text-sm">Your Healthcare Assistant</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Circle className="w-3 h-3 text-green-400 fill-current" />
              <span className="text-sm">Online</span>
            </div>
            {currentUser && (
              <div className="relative profile-dropdown-container">
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className="flex items-center space-x-2 bg-blue-800/50 hover:bg-blue-800/70 px-3 py-2 rounded-lg transition-colors cursor-pointer"
                >
                  <User className="w-4 h-4" />
                  <span className="text-sm">
                    {currentUser.fullName}
                    {currentUser.is_admin && (
                      <span className="ml-1 text-yellow-300">(Admin)</span>
                    )}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Profile Dropdown */}
                {isProfileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 overflow-hidden">
                    {/* Dropdown Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                          <User className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg">{currentUser.fullName}</h3>
                          {currentUser.is_admin && (
                            <div className="flex items-center space-x-1 mt-1">
                              <Shield className="w-3 h-3 text-yellow-300" />
                              <span className="text-xs text-yellow-300">Administrator</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Dropdown Body */}
                    <div className="p-4 space-y-3">
                      {/* Email */}
                      {currentUser.email && (
                        <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                          <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 font-medium mb-1">Email Address</p>
                            <p className="text-sm text-gray-900 font-medium break-all">{currentUser.email}</p>
                          </div>
                        </div>
                      )}

                      {/* Phone Number */}
                      {currentUser.phoneNumber && (
                        <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                          <Phone className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 font-medium mb-1">Phone Number</p>
                            <p className="text-sm text-gray-900 font-medium">{currentUser.phoneNumber}</p>
                          </div>
                        </div>
                      )}

                      {/* User ID */}
                      {currentUser.user_id && (
                        <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                          <User className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 font-medium mb-1">User ID</p>
                            <p className="text-xs text-gray-700 font-mono break-all">{currentUser.user_id}</p>
                          </div>
                        </div>
                      )}

                      {/* Account Type */}
                      <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                        <Shield className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 font-medium mb-1">Account Type</p>
                          <p className="text-sm text-gray-900 font-medium">
                            {currentUser.is_admin ? 'Administrator' : 'Standard User'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Dropdown Footer */}
                    <div className="border-t border-gray-200 p-3">
                      <button
                        onClick={() => setIsProfileDropdownOpen(false)}
                        className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-medium py-2"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <button className="flex items-center space-x-2 bg-blue-800 hover:bg-blue-900 px-3 py-2 rounded-lg transition-colors">
              <Home className="w-4 h-4" />
              <span className="text-sm">Home</span>
            </button>
            {currentUser?.is_admin && (
              <button
                onClick={() => setIsAdminPanelOpen(true)}
                className="flex items-center space-x-2 bg-purple-700 hover:bg-purple-800 px-3 py-2 rounded-lg transition-colors"
                title="Admin Panel (Ctrl+Shift+A)"
              >
                <Settings className="w-4 h-4" />
                <span className="text-sm">Admin</span>
              </button>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 bg-gray-700 hover:bg-gray-800 px-3 py-2 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex gap-6">
          {/* Sidebar - UHID Selector (Left) */}
          {sidebarPosition === 'left' && (
            <div className={`transition-all duration-300 flex-shrink-0 h-[calc(100vh-120px)] ${isSidebarOpen ? 'w-[480px]' : 'w-20'}`}>
              <UHIDSelector
                currentUser={currentUser}
                selectedUHID={selectedUHID}
                selectedPatientDetails={selectedPatientDetails}
                onSelectUHID={handleUHIDSelect}
                onSelectAppointment={handleAppointmentSelect}
                onSelectAppointmentWithoutModal={handleAppointmentSelectWithoutModal}
                refreshTrigger={uhidRefreshTrigger}
                chatHistoryRefreshTrigger={chatHistoryRefreshTrigger}
                onSelectChat={handleSelectChat}
                isOpen={isSidebarOpen}
                position={sidebarPosition}
                onToggle={handleSidebarToggle}
                onPositionChange={handleSidebarPositionChange}
              />
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex-1 transition-all duration-300">
            {/* Welcome Section */}
            <div className="rounded-lg shadow-sm p-6 mb-6" style={{
          backgroundColor: themeSettings?.chatBackgroundColor || '#ffffff',
          borderColor: themeSettings?.borderColor || '#e5e7eb',
          borderWidth: '1px',
          borderStyle: 'solid'
        }}>
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 p-1">
              <img src="/apollo-logo.svg" alt="Apollo Hospitals" className="w-10 h-10 object-contain" />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-lg">👋</span>
                <h2 className="text-xl font-semibold text-gray-800">Welcome to Arogya!</h2>
              </div>
              <p className="mb-4" style={{ color: themeSettings?.textColor || '#4b5563' }}>
                Your simple healthcare assistant for appointments and medical needs.
              </p>
              <div className="text-sm text-gray-500 space-y-1">
                <div><span className="font-medium">Current Date:</span> {formatDate(currentTime)}</div>
                <div><span className="font-medium">Current Time:</span> {formatTime(currentTime)}</div>
              </div>
              <p className="mt-4 font-medium" style={{ color: themeSettings?.textColor || '#374151' }}>
                How can I assist you today?
              </p>
            </div>
          </div>
        </div>

        {/* Chat Messages */}
        {chatMessages.length > 0 && (
          <div className="rounded-lg shadow-sm mb-6" style={{
            backgroundColor: themeSettings?.chatBackgroundColor || '#ffffff',
            borderColor: themeSettings?.borderColor || '#e5e7eb',
            borderWidth: '1px',
            borderStyle: 'solid'
          }}>
            <div className="flex items-center justify-between px-4 py-3" style={{
              borderBottom: `1px solid ${themeSettings?.borderColor || '#e5e7eb'}`
            }}>
              <div className="flex items-center space-x-2">
                <MessageSquare className="w-5 h-5" style={{ color: themeSettings?.textColor || '#4b5563' }} />
                <span className="font-medium" style={{ color: themeSettings?.textColor || '#374151' }}>Chat History</span>
              </div>
              <button
                onClick={handleNewChat}
                className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                title="Start a new chat conversation"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-medium">New Chat</span>
              </button>
            </div>
            <div className="p-4 max-h-96 overflow-y-auto">
              <div className="space-y-4">
                {chatMessages.map((msg, index) => (
                  <div key={index} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`flex ${msg.type === 'user' ? 'flex-row-reverse' : 'flex-row'} items-start space-x-2 max-w-[70%]`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.type === 'user' ? 'bg-blue-600 ml-2' : 'bg-gray-200 mr-2'
                      }`}>
                        {msg.type === 'user' ? (
                          <span className="text-white text-sm font-bold">U</span>
                        ) : (
                          <img src="/apollo-logo.svg" alt="Apollo" className="w-5 h-5 object-contain" />
                        )}
                      </div>
                      <div className="px-4 py-2 rounded-lg chat-message-content" style={{
                        backgroundColor: msg.type === 'user'
                          ? (themeSettings?.userMessageColor || '#2563eb')
                          : (themeSettings?.botMessageColor || '#f3f4f6'),
                        color: msg.type === 'user' ? 'white' : (themeSettings?.textColor || '#1f2937')
                      }}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-xs mt-1 ${
                          msg.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                        }`}>
                          {msg.timestamp.toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex items-start space-x-2 max-w-[70%]">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mr-2 p-1">
                        <img src="/apollo-logo.svg" alt="Apollo" className="w-5 h-5 object-contain" />
                      </div>
                      <div className="bg-gray-100 text-gray-800 px-4 py-2 rounded-lg">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Service Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {serviceButtons.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <button
                key={index}
                onClick={() => handleServiceClick(service.id)}
                className={`${service.color} border-2 rounded-lg p-4 hover:shadow-md transition-all duration-200 hover:scale-105 text-left`}
              >
                <div className="flex items-center space-x-3">
                  <IconComponent className="w-6 h-6" />
                  <span className="font-medium text-sm">{service.text}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Chat Input */}
        <div className="rounded-lg shadow-sm p-4" style={{
          backgroundColor: themeSettings?.chatBackgroundColor || '#ffffff',
          borderColor: themeSettings?.borderColor || '#e5e7eb',
          borderWidth: '1px',
          borderStyle: 'solid'
        }}>
          <div className="flex items-center space-x-3">
            <input
              type="text"
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={handleSendMessage}
              disabled={isLoading}
              className="text-white p-3 rounded-lg transition-colors duration-200"
              style={{
                backgroundColor: isLoading
                  ? '#9ca3af'
                  : (themeSettings?.primaryButtonColor || '#2563eb'),
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
              onMouseEnter={(e) => {
                if (!isLoading && themeSettings?.primaryButtonHoverColor) {
                  e.target.style.backgroundColor = themeSettings.primaryButtonHoverColor;
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.target.style.backgroundColor = themeSettings?.primaryButtonColor || '#2563eb';
                }
              }}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
          </div>
          {/* End of Main Content Area */}

          {/* Sidebar - UHID Selector (Right) */}
          {sidebarPosition === 'right' && (
            <div className={`transition-all duration-300 flex-shrink-0 h-[calc(100vh-120px)] ${isSidebarOpen ? 'w-[480px]' : 'w-20'}`}>
              <UHIDSelector
                currentUser={currentUser}
                selectedUHID={selectedUHID}
                selectedPatientDetails={selectedPatientDetails}
                onSelectUHID={handleUHIDSelect}
                onSelectAppointment={handleAppointmentSelect}
                refreshTrigger={uhidRefreshTrigger}
                chatHistoryRefreshTrigger={chatHistoryRefreshTrigger}
                onSelectChat={handleSelectChat}
                isOpen={isSidebarOpen}
                position={sidebarPosition}
                onToggle={handleSidebarToggle}
                onPositionChange={handleSidebarPositionChange}
              />
            </div>
          )}
        </div>
      </div>

      {/* Patient Registration Modal */}
      <PatientRegistrationModal
        isOpen={isRegistrationModalOpen}
        onClose={() => setIsRegistrationModalOpen(false)}
        onSuccess={handlePatientRegistrationSuccess}
      />

      {/* Appointment Modal */}
      <AppointmentModal
        isOpen={isAppointmentModalOpen}
        onClose={() => setIsAppointmentModalOpen(false)}
        prefilledUHID={selectedUHID}
        prefilledAppointmentId={selectedAppointmentId}
        initialAction={appointmentAction}
        onSuccess={handleAppointmentSuccess}
      />

      {/* My Appointments Modal */}
      <MyAppointmentsModal
        isOpen={isMyAppointmentsModalOpen}
        onClose={() => setIsMyAppointmentsModalOpen(false)}
        prefilledUHID={selectedUHID}
        onSelectAppointment={(appointmentId, action) => {
          setSelectedAppointmentId(appointmentId);
          setAppointmentAction(action);
          if (action) {
            setIsMyAppointmentsModalOpen(false);
            setIsAppointmentModalOpen(true);
          }
        }}
        selectedAppointmentId={selectedAppointmentId}
        selectedAction={appointmentAction}
        currentUser={currentUser}
      />

      {/* Services & Pricing Modal */}
      <ServicesPricingModal
        isOpen={isServicesPricingModalOpen}
        onClose={() => setIsServicesPricingModalOpen(false)}
      />

      {/* Nearby Hospitals Modal */}
      <NearbyHospitalsModal
        isOpen={isNearbyHospitalsModalOpen}
        onClose={() => setIsNearbyHospitalsModalOpen(false)}
      />

      {/* Admin Panel - Press Ctrl+Shift+A to open */}
      <AdminPanel
        isOpen={isAdminPanelOpen}
        onClose={() => setIsAdminPanelOpen(false)}
        onSettingsUpdate={handleSettingsUpdate}
        currentUser={currentUser}
      />
    </div>
  );
};

export default ArogyaUI;